/**
 * @author vishwadeep.kapoor
 * @Date: 2018-08-08 13:25:11
 * @Last Modified by: vishwadeep
 * @Last Modified time: 2020-10-08 12:57:46
 * @desc commonUtils contains all utils that can u used in the different files
 */

import { parse } from 'qs';
import crypto from 'crypto'; // default node package
import moment from 'moment';
import {
  isObject,
  find,
  filter,
  forEach,
  map,
  uniq,
  startsWith,
  findIndex,
  remove,
  sumBy,
  isEmpty,
  cloneDeep,
} from 'lodash';

import {
  DATE_DDMMYYY, DATE_YYYYMMDD, DATE_DDMMMYYYY, DATE_FORWARD, LINK_ZINGER_CARD, LINK_PREPAID_CARD, LINK_REWARD_PLATFROM,
  LINK_SAVE, LINK_EDGE, LINK_SELECT, LINK_PROPEL,
} from './constants';
import endPoints from '../config/endPoints';
import { constants, utils } from '.';

export const lodashFind = (array, obj) => (find(array, obj) || {});
export const lodashFilter = (array, obj) => (filter(array, obj));
export const lodashForEach = (array, obj) => (forEach(array, obj));
export const lodashMap = (array, obj) => (map(array, obj));
export const lodashUniq = (array) => (uniq(array));
export const lodashFindIndex = (array, obj) => (findIndex(array, obj));
export const lodashRemove = (array, obj) => (remove(array, obj));
export const lodashSumBy = (array, key) => (sumBy(array, key));
export const lodashStartsWith = (string, target) => (startsWith(string, target));
export const lodashIsEmpty = (value) => (!isEmpty(value));
export const lodashIsObject = (value) => (isObject(value));
export const parseQueryString = (string) => (parse(string, { ignoreQueryPrefix: true }));
export const openLinkPath = (value) => (window.open(value, '_blank'));
const lodashCloneDeep = (data) => (cloneDeep(data));

export const getMoment = (inputDate, reqFormat) => {
  const dateWithFormat = inputDate ? moment(inputDate).format(reqFormat) : moment().format(reqFormat);
  const dateWithOutFormat = inputDate ? moment(inputDate) : moment();
  return reqFormat ? dateWithFormat : dateWithOutFormat;
};
export const momentTimeCompare = (key1, key2, format) => {
  const time1 = moment(key1, format);
  const time2 = moment(key2, format);
  return time2.isBefore(time1);
};
export const momentTimeChange = (time, req_add_time, req_monent_type) => moment(time).add(req_add_time, req_monent_type);
export const momentRoundTime = (currentTime, rounded_min, format) => {
  const start = moment(currentTime);
  const remainder = rounded_min - (start.minute() % rounded_min);
  return format ? moment(start).add(remainder, 'minutes').format(format) : moment(start).add(remainder, 'minutes');
};

export const roundNumber = (num) => Math.round(num * 100) / 100;
/**
 * @param {boolean} isTwelveHours {optional}
 * @param {*number} spliceFrom {optional}
 * @param {*number} spliceTo {optional}
 * @returns {*array}
 */
export const generateTime = (isTwelveHours, spliceFrom, spliceTo) => {
  const timeAry = [];
  for (let i = 0; i < 24; i += 1) {
    const timeSpan = i > 12 ? 'PM' : 'AM';
    let str = null;
    if (isTwelveHours) {
      str = i >= 13 ? `${i - 12}:00 ${timeSpan}` : `${i}:00 ${timeSpan}`;
    } else { str = `${i}:00`; }
    timeAry.push(str);
  }
  if (spliceFrom && spliceTo) {
    return arraySlice(timeAry, spliceFrom, spliceTo);
  }
  return timeAry;
};

/**
 * @desc updating the keys of the required array;
 * @param {array} payload
 * @return array
 */
export const updateObjectKeys = (array, key_To, key_From) => {
  const values = array && array.map((item) => {
    const _item = item;
    _item[key_To] = _item[key_From];
    return _item;
  });
  return values;
};

/**
 * @desc converting date string to time
 * @param {string} date
 */
export const getTime = (date, is12hours = false) => {
  const today = date ? new Date(date) : new Date();
  const dateTime = today.toLocaleString('en-US', { hour12: is12hours });
  const c_time = dateTime ? dateTime.split(',') : [];
  const currentTime = c_time.length ? c_time[1].trim() : '00:00';
  return currentTime;
};

/**
 * @param {string} date
 * @param {*case} format
 * @return {formattedDate}
 */
export const formattedDate = (date, format = DATE_DDMMYYY, seperator = DATE_FORWARD) => {
  const todayTime = date ? new Date(date) : new Date();
  const month = (todayTime.getMonth() + 1).toString();
  const day = todayTime.getDate().toString();
  const year = todayTime.getFullYear().toString();
  return dateFormatter({ month, day, year, todayTime }, format, seperator);
};

const dateFormatter = ({ month, day, year, todayTime }, format, seperator) => {
  let _month = month.length === 1 ? `0${month}` : month;
  const _day = day.length === 1 ? `0${day}` : day;
  switch (format) {
    case DATE_DDMMYYY:
      return [_day, _month, year].join(seperator);
    case DATE_YYYYMMDD:
      return [year, _month, _day].join(seperator);
    case DATE_DDMMMYYYY: {
      _month = todayTime.toLocaleString('en-us', { month: 'short' });
      return [_day, _month, year].join(' ');
    }
    default:
      return [day, month, year].join(seperator);
  }
};

export const createArrayToObject = (payload, key_To) => {
  const values = payload && payload.length && payload.map((item) => ({ [key_To]: item }));
  return values || [];
};

export const getCodeStatus = (code) => {
  const _code = Number(code);
  switch (_code) {
    case 0:
      return 'Pending';
    case 2:
    case 3:
      return 'Cancelled';
    case 1:
      return 'confirmed';
    case 5:
      return 'old';
    default:
      return 'old';
  }
};

/** @param {string} api @desc util that is hitting the API
 * @param {*string} reason */
export const successResponse = (api, reason, alertType) => ({ api, reason, msg_type: 'success', alertType, isLoading: false });
export const errorResponse = (api, reason, alertType) => ({ api, reason, msg_type: 'error', alertType, isLoading: false });

/** @deprecated @function isLoading */
export const isLoading = (isLoad = false) => ({ isLoading: isLoad });
/**
 * @param {DEFAULT_ACTION} type
 * @param {Boolean} isLoadingStatus
 * @default {isLoadingStatus-{false}}
 * @desc used in loading the reducer
 *  used as removing the dependency of @func defaultAction from all actions.js
 */
export const isReducerLoading = (type, isLoadingStatus = false) => ({ type, payload: isLoadingStatus });
export const arraySplice = (array, from, to) => array.splice(from, to);
export const arraySlice = (array, from, to) => array.slice(from, to);
export const sortStringLocations = (array) => array.sort((a, b) => a.location.localeCompare(b.location, undefined, { caseFirst: 'upper' }));
export const sortStringVariations = (array) => array.sort((a, b) => (a.free_field) - (b.free_field));
export const sortArrayStrings = (array) => array.sort((a, b) => a.localeCompare(b, undefined, { caseFirst: 'upper' }));
export const parseInteger = (number, radix = 10) => parseInt(number, radix);

/**
 * @deprecated @function addAuthorizationHeaders don't use in export
 * @extends addAuthHeaders
 */
export const addAuthorizationHeaders = (token, device_id, client_id) => `Token token=${token};client_key=client_key;device_id=${device_id};client_id=${client_id}`;
export const isImmutableLoggedIn = (store) => (store().getIn(['headerContainer', 'loggedIn']));
export const immutableUserDetails = (store) => (store().getIn(['headerContainer', 'userDetails']));
export const immutableToJS = (payload) => (payload.toJS());
export const isEqual = (fromPayload, toPayload) => (JSON.stringify(fromPayload) === JSON.stringify(toPayload));
/**
 * @deprecated @func addArrayKey
 * @extends mergeKeys
 */
export const addArrayKey = (array, key, value) => array && array.map((item) => {
  const _item = item;
  _item[key] = value;
  return _item;
});
export const mergeKeys = (array, object) => array && array.map((item) => ({ ...item, ...object }));
/**
 * @desc @func addAuthHeaders used for added headers to API
 * @param {immutable} store
 * @deprecated @func addAuthHeaders
 * @see getAuthPrerequist
 */
export const addAuthHeaders = (store, api) => {
  const _api = api;
  const userDetails = immutableToJS(immutableUserDetails(store));
  const { token, device_id } = userDetails;
  if (_api.defaults.headers && !api.defaults.headers.Authorization && token && device_id) {
    _api.defaults.headers.Authorization = addAuthorizationHeaders(token, device_id);
    return _api;
  }
  return _api;
};

/**
 * @deprecated @function removeAuthHeaders
 * @see getAuthPrerequist
 */
export const removeAuthHeaders = (api) => {
  const _api = api;
  delete _api.defaults.headers.Authorization;
  return _api;
};

export const getAuthPrerequist = (store, isAuth = false, isPlainJS = false) => {
  const tokenAndDevice = getTokenAndDeviceId(store);
  return { ...tokenAndDevice, isAuth, isPlainJS };
};

const getTokenAndDeviceId = (store) => {
  const { token, device_id, client_id } = immutableToJS(immutableUserDetails(store));
  return { token, device_id, client_id };
};
export const checkObjectValues = (payload) => {
  const _payload = payload;
  Object.keys(_payload).forEach((key) => (!_payload[key] || _payload[key] === '[]') && delete _payload[key]);
  return _payload;
};
export const convertCentsToAmout = (amount) => (amount ? (parseFloat(amount) / 100) : 0);
export const convertAmountToCents = (amount) => (amount ? (parseFloat(amount) * 100) : 0);

export const showAlert = (message) => (alert(message)); // eslint-disable-line no-alert
/**
 * @param {object} history
 * @param {*object} payload
 * @example optional
       {
         pathname: '/',
         search: `?s=${searchValue}`,
         state: { prePath: pathname },
      }
 */
export const forceRedirect = (history = {}, payload) => (history.push(payload));
/**
 * @param window
 * @ignore SSR
 * @returns {hostname}
 */
export const getBaseUrl = () => { // eslint-disable-line consistent-return
  const isBrowser = typeof window === 'object';
  if (isBrowser) {
    return window.location.origin;
  }
};
export const getWindowInstance = () => {
  const isBrowser = typeof window === 'object';
  if (isBrowser) {
    return window;
  }
  return {};
};

export const isLocalhostInstance = () => {
  const { location: { hostname = '' } = {} } = getWindowInstance();
  if (hostname === 'localhost') {
    return true;
  }
  return false;
};
/**
 * @param pathname
 * @param search
 * @ignore SSR
 * @returns redired to old website
 * @see /snapdeal||/shopclues path not available on old.website
 */
export const isRedirectedReq = ({ pathname, search = '', platform = '' }) => { // eslint-disable-line consistent-return
  const isBrowser = typeof window === 'object';
  if (isBrowser) {
    const indexRewardCardToken = search.indexOf('client_id');
    // const isBlogPath = pathname ? pathname.includes('/blog') : false;
    if (indexRewardCardToken !== -1) {
      window.location.replace(`https://rewards.zaggle.in/${pathname}${search}`);
    } else if (pathname === '/corporate') {
      window.location.replace('https://corporate.zaggle.in');
    } else if ((pathname === '/snapdeal') || (pathname === '/shopclues')) {
      window.location.replace(`https://old.zaggle.in${pathname}`);
    } else if (platform === 'propel') {
      window.location.replace('https://www.zagglepropel.com/');
    } else if (pathname === LINK_ZINGER_CARD) {
      window.location.replace('https://www.zaggle.in/expense-management.html');
    } else if (pathname === LINK_PREPAID_CARD) {
      window.location.replace('https://www.zaggle.in/corporate-gift-cards.html');
    } else if (pathname === LINK_REWARD_PLATFROM) {
      window.location.replace('https://www.zaggle.in/employee-rewards.html');
    } else if (platform === 'save') {
      window.location.replace(`https://www.zagglesave.com/verification${pathname}${search}`);
    } else if (pathname === '/save') {
      window.location.pathname = LINK_SAVE;
    } else if (pathname === '/edge') {
      window.location.pathname = LINK_EDGE;
    } else if (pathname === '/propel') {
      window.location.pathname = LINK_PROPEL;
    } else if (pathname === '/select') {
      window.location.pathname = LINK_SELECT;
    // } else if (pathname === '/corporate-gifting' || pathname === '/corporate-gifting/') {
      // window.location.replace(LINK_EDGE);
    } else {
      return true;
    }
  }
};
/**
 * @example
          return ['a', 'b'].indexOf('a')
 * @param {*array}
 * @param {*value} string
 * @returns {index} number
 * @case index !== -1
        return undefined
 */
export const IndexOf = (array, value) => { // eslint-disable-line consistent-return
  const index = array.indexOf(value);
  if (index !== -1) {
    return { index, isFind: true };
  }
  return { isFind: false };
};
/**
 * @desc removing the @arg {'%'} from URL
 * @desc double encoding as react router onces decode in history creation
 * */
export const encodeURL = (urlPrifix) => {
  if (!urlPrifix) {
    return urlPrifix;
  }
  const preUrl = urlPrifix.replace(/ /g, '-');
  return preUrl.replace(/%/g, '-percent');
};

export const decodeURL = (url) => {
  if (!url) {
    return url;
  }
  const preUrl = url.replace(/-/g, ' ');
  return preUrl.replace(/ percent/g, '%');
};
/**
 * @desc Getting query params from url
 * @todo copy of the @function parseQueryString
 * */
export const splitURL = ({ search }) => {
  const hashes = search ? search.slice(search.indexOf('?') + 1).split('&') : [];
  const params = {};
  hashes.map((hash) => {
    const [key, val] = hash.split('=');
    params[key] = decodeURIComponent(val);
  });
  return params;
};

export const stringReplace = (string, match, value) => {
  const _match = new RegExp(match, 'g');
  return string.replace(_match, value);
};

/**
 * @function fieldValidations
 * @desc Specificlly written for field Validators
 * @param {object} validators
 */

const pickObjectKey = (object, key) => {
  const array = [];
  Object.keys(object).forEach((item) => array.push(object[item][key]));
  return array;
};

export const onlyNumbers = (string) => (string.replace(/\D/g, ''));
export const onlyStrings = (string) => (string.replace(/\d/g, ''));

export const isFieldRequired = (validators, arr = []) => {
  const errorsArray = pickObjectKey(SORTOUT_VALIDATORS(arr, validators), 'errors');
  const data = lodashFilter(errorsArray, lodashIsEmpty);
  if (data.length) {
    return true;
  }
  return false;
};

export const getDocumentHeight = (id) => document && document.getElementById(id) ? document.getElementById(id).clientHeight : 0;

/**
 * @param {*object} validators
 * @param {*string} fieldName
 * @param {*string} value
 * @param {*object} otherParams optional
 */
export const updateValidators = (validators, fieldName, value, otherParams = {}) => {
  const _validators = validators;
  if (_validators[fieldName]) {
    const { min, max, message, password } = otherParams;
    _validators[fieldName].errors = [];
    _validators[fieldName].state = value;
    _validators[fieldName].valid = true;
    validators[fieldName].rules.forEach((rule) => {
      if (rule.test instanceof RegExp) {
        if (!rule.test.test(value)) {
          const _message = message || rule.message;
          validators[fieldName].errors.push(_message);
          _validators[fieldName].valid = false;
        }
      } else if (typeof rule.test === 'function') {
        if (!rule.test(value, password || min, max)) {
          let _message = '';
          if (message) {
            _message = stringReplace(message, ':min', min);
            _message = stringReplace(_message, ':max', max);
          } else {
            _message = rule.message;
          }
          validators[fieldName].errors.push(_message);
          _validators[fieldName].valid = false;
        }
      }
    });
  }
  return _validators;
};

export const resetValidators = (validators) => {
  const _validators = validators;
  if (_validators) {
    Object.keys(validators).forEach((fieldName) => {
      if (_validators[fieldName].isFieldRequired) {
        _validators[fieldName].errors = ['This field is required'];
      } else {
        _validators[fieldName].errors = [];
      }
      _validators[fieldName].state = '';
      _validators[fieldName].valid = false;
    });
  }
  return _validators;
};

export const checkValidations = (mandatoryFields, originalFields) => {
  for (let i = 0; i < mandatoryFields.length; i += 1) {
    if (!originalFields[mandatoryFields[i]]) {
      return false;
    } else if (originalFields[mandatoryFields[i]] === '') {
      return false;
    }
  }
  return true;
};

/**
 * @method 1 @desc if there is mandatory field then check that condition only
 * @method 2 @desc if error is there but not a mandatory field
 * @method 3 @link 1 and 2 in all the condtion
 * @param {array} mandatory_fields
 * @param {*object} validators
 * @return {array_of_filtered_validators}
 */
export const SORTOUT_VALIDATORS = (mandatory_fields, validators) => {
  const result = [];
  if (mandatory_fields.length) {
    for (let i = 0; i < mandatory_fields.length; i += 1) {
      if (validators[mandatory_fields[i]]) {
        result.push(validators[mandatory_fields[i]]);
      }
    }
    // return result;
  }
  Object.keys(validators).forEach((item) => {
    if (validators[item].isFieldRequired === false && validators[item].errors.length) {
      result.push(validators[item]);
    }
  });
  return result;
};

const getEncryptedData = (text, dynamic_id) => {
  const key = dynamic_id.substring(0, 32);
  const iv = dynamic_id.substring(0, 16);
  const cipher = crypto.createCipheriv('aes-256-cbc', Buffer.from(key), Buffer.from(iv));
  let encrypted = cipher.update(text, 'utf8');
  encrypted = Buffer.concat([encrypted, cipher.final()]);
  return { iv: iv.toString('hex'), encryptedData: encrypted.toString('hex') };
};

// function decrypt(text) {
//   let encryptedText = Buffer.from(text, 'hex');
//   let decipher = crypto.createDecipheriv('aes-256-cbc', Buffer.from(key), Buffer.from(iv));
//   let decrypted = decipher.update(encryptedText);
//   decrypted = Buffer.concat([decrypted, decipher.final()]);
//   return decrypted.toString();
// }

const hex2ascii = (hexx) => {
  const hex = hexx.toString();
  let str = '';
  for (let i = 0; (i < hex.length && hex.substr(i, 2) !== '00'); i += 2) {
    str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
  }
  return str;
};

const getFinalEncryption = (encrypted_data) => {
  const password_ascii = hex2ascii(encrypted_data.encryptedData);
  const base64 = btoa(`${encrypted_data.iv}${password_ascii}`);
  return base64;
};

const validatePin = (text) => {
  const rex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[d$@$!%*?&#])[A-Za-z\\dd$@$!%*?&#]{8,}');
  return rex.test(text);
};

const getDeviceId = () => crypto.randomBytes(16).toString('hex');

const GetRedirectPath = (value) => {
  const valueArray = value ? value.split('/') : [];
  const path = valueArray.length ? valueArray[valueArray.length - 1] : '';
  if (path && path !== '/') {
    return path;
  }
  return valueArray.length ? valueArray[valueArray.length - 2] : '';
};

const GetTimeDifference = (startDate, toDate, type) => {
  const _startDate = moment(startDate);
  const _toDate = toDate ? moment(toDate) : moment();
  return _toDate.diff(_startDate, type);
};

const objectToFormData = (obj, form, namespace) => {
  const fd = form || new FormData();
  let formKey;
  for (const property in obj) { // eslint-disable-line no-restricted-syntax
    if (obj.hasOwnProperty(property)) { // eslint-disable-line no-prototype-builtins
      if (namespace) {
        formKey = `${namespace}[${property}]`;
      } else {
        formKey = property;
      }
      // if the property is an object, but not a File,
      // use recursivity.
      if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
        objectToFormData(obj[property], fd, formKey);
      } else {
        // if it's a string or a File object
        fd.append(formKey, obj[property]);
      }
    }
  }
  return fd;
};

const fileValidation = (file, { fileSize = 104857600, fileType = '', isFileValidation = false }) => {
  if (file.size >= fileSize) {
    file.flag = true;
    file.error = 'size';
  } else if (isFileValidation && !fileType.replace(/\./g, '').split(',').includes(file.type.split('/')[1])) {
    file.flag = true;
    file.error = 'type';
  }
  return false;
};

const momentInstance = (params, format) => moment(params, format);

const disableDate = (current) => current && current > moment().endOf('day');

const disablePastDate = (current) => current && current < moment().endOf('day');

const getDecryptedData = (data, id) => {
  const decoded = atob(data);
  const key = id.substring(0, 32);
  const iv = decoded.substring(0, 16);
  const encrypted_data = decoded.substring(16, decoded.length);
  const decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);
  let decrypt = decipher.update(encrypted_data, 'base64', 'utf8');
  decrypt += decipher.final('utf8');
  return decrypt;
};

const formatExpiryDate = (expiryDate) => {
  const expiry = new Date(expiryDate);
  const month = (expiry.getMonth() + 1).toString().padStart(2, '0');
  const year = expiry.getFullYear().toString().slice(2);
  const formattedExpiry = `${month}/${year}`;
  return formattedExpiry;
};

const capitalFirstCharacter = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

const getLogoutRedirectPath = (client_series, landing_page_url) => {
  switch (client_series) {
    case endPoints.LYRA_SERIES:
      return constants.LYRA_PG_LOGIN;
    default:
      if (landing_page_url) {
        return `/${utils.GetRedirectPath(landing_page_url)}`;
      }
      return '/';
  }
};

export {
  getEncryptedData, getFinalEncryption, validatePin, getDeviceId, GetRedirectPath,
  GetTimeDifference, objectToFormData,
  fileValidation,
  lodashCloneDeep,
  momentInstance,
  disableDate,
  disablePastDate,
  getDecryptedData,
  formatExpiryDate,
  capitalFirstCharacter,
  getLogoutRedirectPath,
};



// WEBPACK FOOTER //
// client/devUtils/commonUtils.js